import { useSelector } from 'react-redux';
import { checkAcl } from '@flow/libs/utils/acl';
import {
  hasActivePlaybook,
  isExtensionEnabled,
  canEditPlaybook,
} from '@app/components/pages/ai-insights/content/utils';
import { AiReviewMenuItem } from '@app/components/menu-items/ai-review';
import { MenuItem } from '@app/components/menu-item';
import AiSparkleIcon from '@app/components/icons/ai-sparkle';
import { getCurrentWorkspaceSelector } from '@app/reducers/app';
import { getAccountFromSessionSelector } from '@app/reducers/session';
import Tooltip from '@app/components/tooltip';
import style from '@app/components/menu-items/ai-review/ai-review.module.scss';
import { T } from '@app/components/translation';

export type Props = {
  agreement: Oneflow.Agreement;
  location: string;
  visible?: boolean;
};

const reusedProps = {
  rel: 'noopener noreferrer',
  target: '_blank',
  className: style.AiReviewButton,
  icon: AiSparkleIcon,
};

const AiReviewComponent = ({ location, agreement, visible }: Props) => {
  const canViewReview = checkAcl(agreement.acl, 'agreement:ai_review:view');
  const account = useSelector(getAccountFromSessionSelector);
  const currentWorkspace = useSelector(getCurrentWorkspaceSelector);
  const canEnableExtension = checkAcl(account.acl, 'account:admin:extension');
  const link = `/api/ext/ai_review/redirect/agreement/${agreement.id}?highlight_index_for_url=1`;

  if (!visible) {
    return null;
  }

  if (!isExtensionEnabled(account)) {
    if (canEnableExtension) {
      return (
        <MenuItem
          {...reusedProps}
          href="/marketplace/ai-insights"
          label={
            <T
              _str="Enable {feature}"
              _comment="Label for if AI Review is disabled for the account and the user can enable it."
              feature="AI Review"
            />
          }
          trackable={{
            name: 'Go To Marketplace Card',
            props: {
              location,
              key: 'ai_review',
            },
          }}
        />
      );
    }
    if (!canEnableExtension) {
      return (
        <MenuItem
          {...reusedProps}
          href="/marketplace/ai-insights"
          label={
            <T
              _str="Explore {feature}"
              _comment="Label for if AI Review is disabled for the account and the user can't enable it."
              feature="AI Review"
            />
          }
          trackable={{
            name: 'Go To Marketplace Card',
            props: {
              location,
              key: 'ai_review',
            },
          }}
        />
      );
    }
  }

  if (!hasActivePlaybook(agreement.collection)) {
    if (!canEditPlaybook(currentWorkspace)) {
      return (
        <Tooltip
          side="top"
          touchable
          zIndex={31}
          message={
            <T
              _str="Contact the workspace admin to set up {feature}."
              _comment="tooltip to let users now that the ai review feature has not been set up for their workspace"
              feature="AI Review"
            />
          }
        >
          <AiReviewMenuItem disabled />
        </Tooltip>
      );
    }
    if (canEditPlaybook(currentWorkspace)) {
      return (
        <MenuItem
          {...reusedProps}
          label={
            <T
              _str="Set up playbook"
              _comment="Label for button that navigates user to set up AI review for their workspace."
            />
          }
          href={`/admin/workspaces/${currentWorkspace.id}/ai-review`}
        />
      );
    }
  }

  if (canViewReview) {
    return (
      <AiReviewMenuItem
        href={link}
        location={location}
      />
    );
  }

  return null;
};

export default AiReviewComponent;
